import { useState, useEffect, useMemo, forwardRef } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DataverseEntityInfo } from "@shared/api-types";

interface EntityComboboxProps {
  entities: DataverseEntityInfo[] | undefined;
  value: string;
  onChange: (value: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
}

export const EntityCombobox = forwardRef<HTMLButtonElement, EntityComboboxProps>(function EntityCombobox(
  {
    entities,
    value,
    onChange,
    isLoading,
    disabled = false,
    placeholder = "Select an entity...",
    name
  },
  ref
) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredEntities = useMemo(() => {
    if (!entities) return [];

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const validEntities = entities.filter(entity => entity.LogicalName);

    if (!normalizedQuery) {
      return validEntities;
    }

    return validEntities.filter(entity => {
      const logical = entity.LogicalName?.toLowerCase() || (entity as { logicalName?: string }).logicalName?.toLowerCase() || "";
      const schema = entity.SchemaName?.toLowerCase() || (entity as { schemaName?: string }).schemaName?.toLowerCase() || "";

      const displayNameRaw = (entity as any)?.DisplayName;
      const display = (() => {
        if (!displayNameRaw) return "";
        if (typeof displayNameRaw === "string") return displayNameRaw.toLowerCase();
        const localized = displayNameRaw?.UserLocalizedLabel?.Label || displayNameRaw?.LocalizedLabels?.[0]?.Label;
        return (localized || "").toLowerCase();
      })();

      const fallbackDisplay = ((entity as { displayName?: string })?.displayName || "").toLowerCase();

      return (
        logical.includes(normalizedQuery) ||
        schema.includes(normalizedQuery) ||
        display.includes(normalizedQuery) ||
        fallbackDisplay.includes(normalizedQuery)
      );
    });
  }, [entities, searchQuery]);

  const displayedEntities = filteredEntities.slice(0, 200);

  const selectedEntity = entities?.find(entity => entity.LogicalName === value);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          name={name}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between bg-background px-3 font-normal outline-offset-0 hover:bg-background focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20",
            !value && "text-muted-foreground"
          )}
        >
          {isLoading ? (
            <div className="flex items-center">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading entities...
            </div>
          ) : (
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {selectedEntity
                ? (selectedEntity.SchemaName || selectedEntity.LogicalName)
                : placeholder
              }
            </span>
          )}
          <ChevronDown
            size={16}
            strokeWidth={2}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search entities..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {displayedEntities.length === 0 ? (
              <CommandEmpty>No entities found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {displayedEntities.map((entity) => (
                  <CommandItem
                    key={entity.LogicalName}
                    value={entity.LogicalName}
                    keywords={[entity.SchemaName || '', entity.LogicalName || '']}
                    onSelect={(currentValue) => {
                      onChange(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                    className="cursor-pointer bg-white border mb-1 hover:bg-accent/20 hover:text-accent-foreground [data-highlighted]:bg-accent [data-highlighted]:text-accent-foreground [data-selected]:bg-accent/10 [data-selected]:text-accent-foreground"
                  >
                    <div className="flex flex-col w-full">
                      <span className="font-medium text-foreground">{entity.SchemaName || entity.LogicalName}</span>
                      {entity.SchemaName && entity.LogicalName !== entity.SchemaName && (
                        <span className="text-xs text-muted-foreground/80">{entity.LogicalName}</span>
                      )}
                    </div>
                    {value === entity.LogicalName && (
                      <Check size={16} strokeWidth={2} className="ml-auto text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});

